import dayjs from "dayjs";

import { JobCategory, User } from "@/api/generated";

export type ProfileSection = "personal" | "financial" | "investment";
export const JOB_INDUSTRY_REQUIRED_CATEGORIES = [
  JobCategory.会社員上場,
  JobCategory.会社員非上場,
  JobCategory.会社員外資系企業,
];

export const isJobIndustryRequired = (category: unknown) =>
  JOB_INDUSTRY_REQUIRED_CATEGORIES.includes(category as JobCategory);

const isFilled = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (value == null || value === false) return false;
  return typeof value === "string" ? value.trim() !== "" && value.trim() !== "未選択" : true;
};

const count = (rows: unknown[]) => {
  const completed = rows.filter(isFilled).length;
  return { completed, total: rows.length, remaining: rows.length - completed };
};

/** 保存済み値と編集中の値で、同じ必須行を数える。任意・非表示行は分母に含めない。 */
export const calculateProfileCompletion = (values: Record<string, unknown>) => {
  const personal = count([
    values.family_name,
    values.first_name,
    values.family_name_kana,
    values.first_name_kana,
    Number(values.birth_year) > 0 && Number(values.birth_month) > 0,
    values.phone_number,
    values.address_prefecture,
  ]);
  const financialRows = [
    values.job_category,
    values.job_continuous_period,
    values.annual_income,
    values.financial_asset,
    values.holding_property_count,
    Number(values.current_residence_type_id) > 0,
  ];
  if (isJobIndustryRequired(values.job_category)) financialRows.push(values.job_industry);
  if (Number(values.holding_property_count) > 0) financialRows.push(values.real_estate_loan_amount);
  if (Number(values.current_residence_type_id) === 1) financialRows.push(values.home_loan_amount);
  if (Number(values.current_residence_type_id) === 2) financialRows.push(values.monthly_rent_payment_amount);
  const financial = count(financialRows);
  const investment = count([values.interested_property_type_ids]);
  const total = personal.total + financial.total + investment.total;
  const completed = personal.completed + financial.completed + investment.completed;
  return {
    sections: { personal, financial, investment },
    total,
    completed,
    remaining: total - completed,
    rate: Math.floor((completed / total) * 100),
  };
};

export const getProfileCompletionFromUser = (user: User) => {
  const birthday = user.birthday ? dayjs(user.birthday) : null;
  return calculateProfileCompletion({
    ...user,
    birth_year: birthday?.isValid() ? birthday.year() : "",
    birth_month: birthday?.isValid() ? birthday.month() + 1 : "",
  });
};
